import React, { FC, useRef, useCallback, useEffect } from 'react';
import { HttpRequestType, HttpResponse, HttpResponseBody } from 'bitmovin-player';
import { parse, stringify } from 'hls-parser';
import { MediaPlaylist } from 'hls-parser/types';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import { PlayerWrapper } from './playerWrapper';
import awsConfig from '../../../aws-exports';
import { DRMTYPE } from '../../../utils/types';

import './videoPlayer.css';

dayjs.extend(isBetween);

const SEGMENT_VARIANCE = 5;
const TIMESHIFT_PLAYLIST_TYPE = 'VOD';

/**
 * Configuration object for the Bitmovin video player.
 *
 * @constant
 * @type {Object}
 * @property {string} key - The license key for Bitmovin, retrieved from AWS configuration.
 * @property {Object} playback - Playback settings for the video player.
 * @property {boolean} playback.muted - Indicates whether the video should start muted.
 * @property {Object} style - Styling options for the video player.
 * @property {string} style.aspectratio - The aspect ratio of the video player.
 * @property {boolean} style.ux - Indicates whether the default Bitmovin UX should be used.
 * @property {string} style.width - The width of the video player.
 */
const BITMOVINCONFIG = {
	key: awsConfig.bitmovinLicenseKey,
	playback: {
		muted: true
	},
	style: {
		aspectratio: '16:9',
		ux: false,
		width: '100%'
	}
};

/**
 * VideoPlayer component is a wrapper around the Bitmovin player.
 * It initializes and configures the player with the provided props.
 *
 * @param {VideoPlayerProps} props - The properties for the VideoPlayer component.
 * @param {boolean} [props.autoplay=true] - Determines if the video should autoplay.
 * @param {DRMTYPE} [props.drmType] - The type of DRM to be used.
 * @param {string} [props.height='400px'] - The height of the video player.
 * @param {string} [props.laSource] - The license acquisition source for DRM.
 * @param {string} [props.source] - The source URL of the video.
 * @param {number} [props.videoEnd] - The end time of the video.
 * @param {number} [props.videoStart] - The start time of the video.
 *
 * @returns {JSX.Element} The VideoPlayer component.
 *
 * @example
 * <VideoPlayer
 *   autoplay={false}
 *   drmType={DRMTYPE.WIDEVINE}
 *   height="500px"
 *   laSource="https://example.com/license"
 *   source="https://example.com/video.mp4"
 *   timeShift={true}
 *   videoEnd={120}
 *   videoStart={10}
 * />
 */
export const VideoPlayer: FC<VideoPlayerProps> = (props) => {
	const { autoplay = true, drmType, height = '400px', laSource, source, timeShift = true, videoEnd, videoStart } = props;
	const playerEl = useRef<HTMLDivElement>(null);
	const player = useRef<PlayerWrapper>();

	const preTime = dayjs(videoStart).subtract(SEGMENT_VARIANCE, 's');
	const postTime = dayjs(videoEnd).add(SEGMENT_VARIANCE, 's');

	// The timeShiftPreprocessor is an optionally included preprocessor that filters out segments that are outside of the
	// specified time range. This is used to create a time-shifted playlist for a stream where we can't rely on the legacy
	// method of querying MediaPackage for specific parts of a stream.
	const timeShiftPreprocessor = {
		preprocessHttpResponse: async <T extends HttpResponseBody>(type: HttpRequestType, response: HttpResponse<T>): Promise<HttpResponse<T>> => {
			if (response.body && type === HttpRequestType.MANIFEST_HLS_VARIANT) {
				// Parse the HLS playlist in the response and filter out segments that are outside of the desired time range.
				const hlsPlayList = parse(response.body.toString()) as MediaPlaylist;

				if (hlsPlayList.segments.length > 0) {
					hlsPlayList.segments = hlsPlayList.segments.filter(({ programDateTime }) => {
						return dayjs(programDateTime).isBetween(preTime, postTime, 'second', '[]');
					});

					// Set the playlist type to VOD (Video On Demand) to prevent the player from trying to load live segments.
					hlsPlayList.playlistType = TIMESHIFT_PLAYLIST_TYPE;

					// Return the modified playlist as the response body.
					response.body = stringify(hlsPlayList) as T;
				}
			}

			return response;
		}
	};

	const setupPlayer = useCallback(() => {
		if (!player.current) {
			const playerConfig = {
				...BITMOVINCONFIG,
				style: { ...BITMOVINCONFIG.style, height },
				network: timeShift ? timeShiftPreprocessor : undefined
			};
			const playerInstance = new PlayerWrapper(playerEl.current!, playerConfig);
			player.current = playerInstance;
		}
	}, [height, timeShift, timeShiftPreprocessor]);

	useEffect(() => {
		setupPlayer();
	}, [setupPlayer]);

	useEffect(() => {
		if (source) {
			player.current!.init(source, videoStart, videoEnd, autoplay, drmType, laSource, timeShift);
		}
	}, [source, videoStart, videoEnd, autoplay, drmType, laSource, timeShift]);

	useEffect(() => {
		return (): void => {
			player.current!.destroy();
		};
	}, []);

	return <div ref={playerEl}></div>;
};

type VideoPlayerProps = {
	autoplay?: boolean;
	drmType?: DRMTYPE;
	height?: string;
	laSource?: string;
	source: string;
	timeShift?: boolean;
	videoEnd?: string;
	videoStart?: string;
};

export default VideoPlayer;
